import React, { useState } from 'react';
import { Container, Row, Col, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt,
  faShieldAlt,
  faCheckCircle,
  faTimes,
  faCode
} from '@fortawesome/free-solid-svg-icons';
import './Footer.scss';

const LogoWhite = `${process.env.PUBLIC_URL}/images/Logo-white.png`;


/**
 * Footer component for MET Connect® website
 * 
 * Provides navigation links, contact information, newsletter signup,
 * and copyright information at the bottom of every page
 * 
 * @returns {JSX.Element} The Footer component
 */
const Footer = () => {
  const [showCookiesModal, setShowCookiesModal] = useState(false);

  const handleCookiesModalOpen = () => setShowCookiesModal(true);
  const handleCookiesModalClose = () => setShowCookiesModal(false);

  return (
    <footer className="footer">      
      {/* Main footer content */}
      <div className="main-footer" style={{
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background image overlay */}
        <div className="main-footer-bg" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/night-2.jpg)`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          opacity: 0.25,
          zIndex: 0
        }}></div>
        <Container>
          <Row>
            {/* Company info */}
            <Col lg={4} md={4} className="mb-4 mb-lg-0">
              <div className="footer-brand d-flex align-items-center">
                <img src={LogoWhite} alt="MET Connect® Logo" className="footer-logo me-3" />
                <div className="text-logo footer-text-logo">
                  <div className="logo-main-text">MET Connect<sup>®</sup></div>
                  <div className="logo-sub-text">Connected Since 2010</div>
                </div>
              </div>
              <p className="about-company">
                MET Connect® provides secure remote access and monitoring solutions
                for industrial control systems, helping businesses improve efficiency
                and security.
              </p>
            </Col>
            
            {/* Quick links */}
            <Col lg={4} md={4} className="mb-4 mb-lg-0">
              {/* Only used as a spacer */}
            </Col>
            
            {/* Contact info */}
            <Col lg={4} md={4} className="mb-4 mb-lg-0">
              <h4 className="footer-heading">Contact Us</h4>
              <ul className="contact-info">
                <li>
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
                  <span>
                    Australian Software Distribution Pty. Ltd.<br />
                    205/10 Albert Ave., BROADBEACH<br />
                    QLD 4218, Australia
                  </span>
                </li>
                <li>
                  <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                  <span>+61 (0)7 3333 23 53</span>
                </li>
                <li>
                  <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                  <span>support@met-connect.com</span>
                </li>
              </ul>
            </Col>
          </Row>
          <Row>
            {/* Copyright section styled like mini footer */}
            <div className="copyright">
              <div className="horizontal-divider"></div>
              <Container>
                <Row className="align-items-center">
                  <Col md={4} className="text-center text-md-start">
                    <p className="mb-0">&copy; {new Date().getFullYear()} MET Connect®. All rights reserved.</p>
                  </Col>
                  <Col md={4} className="text-center">
                    <p className="footer-credit">
                      <a href="https://jamesboon.dev/" target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faCode} className="me-2" />
                        Website by jamesboon.dev
                      </a>
                    </p>
                  </Col>
                  <Col md={4} className="text-center text-md-end">
                    <ul className="legal-links">
                      <li><button className="btn-link" onClick={handleCookiesModalOpen}>Cookie Policy</button></li>
                    </ul>
                  </Col>
                </Row>
              </Container>
            </div>
          </Row>
        </Container>
      </div>
      


      {/* Cookies Modal */}
      <Modal show={showCookiesModal} onHide={handleCookiesModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
            Cookie Policy
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="align-items-center mb-4">
            <Col xs={12} md={8}>
              <h5>We Respect Your Privacy</h5>
              <p>
                At MET Connect<sup>®</sup>, we prioritize your privacy and firmly believe in your right to browse the web without being tracked.
              </p>
            </Col>
            <Col xs={12} md={4} className="text-center">
              <div className="privacy-icon">
                <FontAwesomeIcon icon={faCheckCircle} size="4x" className="text-success mb-2" />
              </div>
            </Col>
          </Row>
          
          <div className="cookie-policy-highlight p-3 mb-4">
            <p className="mb-0">
              <strong>We do not use cookies or any tracking technologies on this website.</strong>
            </p>
          </div>
          
          <p>
            Our website is designed to provide you with information about our industrial control system solutions
            without collecting any personal data or tracking your browsing behavior.
          </p>
          
          <p>
            We believe that your online experience should be:
          </p>
          
          <ul className="privacy-benefits">
            <li><FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" /> Private</li>
            <li><FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" /> Secure</li>
            <li><FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" /> Free from unnecessary data collection</li>
          </ul>
          
          <p>
            This approach aligns with our commitment to security and reliability in all aspects of our business.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCookiesModalClose}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </footer>
  );
};

export default Footer;